import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface NetworkLink {
  href: string;
  label: string;
  description: string;
}

/**
 * SeoNetworkLinks — bloc volontairement invisible pour les visiteurs
 * (classe `sr-only`, jamais `display:none`) mais parfaitement lisible par
 * les robots d'indexation. Il expose les liens vers les sites déjà créés
 * sur Zifek afin qu'ils soient découverts et référencés par Google, même
 * lorsqu'ils vivent sur un sous-domaine.
 */
export default function SeoNetworkLinks() {
  const [links, setLinks] = useState<NetworkLink[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const found: NetworkLink[] = [];
      try {
        // 1. Sites marchands actifs (vitrines + boutiques).
        const { data: sites } = await supabase
          .from('userwebsite')
          .select('id, user_name, categorie_nom')
          .order('id', { ascending: false })
          .limit(200);
        (sites || []).forEach((row: Record<string, unknown>) => {
          const userName = String(row.user_name || '').trim();
          if (!userName) return;
          found.push({
            href: `https://zifek.fr/business/${encodeURIComponent(userName)}`,
            label: userName,
            description: row.categorie_nom ? `Site ${String(row.categorie_nom)} sur Zifek` : 'Site marchand sur Zifek',
          });
        });
      } catch {
        /* table optionnelle */
      }

      try {
        // 2. Domaines personnalisés vérifiés.
        const { data: domains } = await supabase
          .from('websitedomain')
          .select('id, domaine, verified, is_active')
          .limit(200);
        (domains || []).forEach((row: Record<string, unknown>) => {
          const domain = String(row.domaine || '').trim();
          if (!domain || row.verified === false) return;
          if (row.is_active === false) return;
          found.push({
            href: `https://${domain}`,
            label: domain,
            description: 'Boutique en ligne sur domaine personnalisé',
          });
        });
      } catch {
        /* table optionnelle */
      }

      // Dédoublonnage
      const unique = new Map<string, NetworkLink>();
      found.forEach((l) => {
        if (!unique.has(l.href)) unique.set(l.href, l);
      });
      if (alive) setLinks(Array.from(unique.values()));
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (links.length === 0) return null;

  return (
    <nav aria-label="Sites créés avec Zifek" className="sr-only">
      <h2>Boutiques et sites créés avec Zifek</h2>
      <p>
        Découvrez les commerces, artisans et boutiques en ligne qui utilisent Zifek au Maroc :
      </p>
      <ul>
        {links.map((l) => (
          <li key={l.href}>
            <a href={l.href} title={`${l.label} — ${l.description}`} rel="noopener">
              {l.label}
            </a>
            <span> — {l.description}</span>
          </li>
        ))}
      </ul>
    </nav>
  );
}